import { PlayerCourseActivityPage } from '@hendrx/core/courses';
import {
  courseLearningObjectTypeQueryKey,
  getCourseLearningObjectType
} from '@hendrx/core/courses/queries/getCourseLearningObjectType';
import {
  getLatestLearningActivitySnapshotId,
  latestLearningActivitySnapshotIdQueryKey
} from '@hendrx/core/learningActivity/queries/getLatestLearningActivitySnapshotId';
import {
  getLearningActivitySnapshotById,
  learningActivitySnapshotByIdQueryKey
} from '@hendrx/core/learningActivity/queries/getLearningActivitySnapshotById';
import { getLatestPBLSnapshotId, latestPBLSnapshotIdQueryKey } from '@hendrx/core/pbl/queries/getLatestPBLSnapshotId';
import { getPBLSnapshotById, pblSnapshotByIdQueryKey } from '@hendrx/core/pbl/queries/getPBLSnapshotById';
import {
  getLatestPlenarySnapshotId,
  latestPlenarySnapshotIdQueryKey
} from '@hendrx/core/plenary/queries/getLatestPlenarySnapshotId';
import {
  getPlenarySnapshotById,
  plenarySnapshotByIdQueryKey
} from '@hendrx/core/plenary/queries/getPlenarySnapshotById';
import { LearningObjectTypes } from '@hendrx/lms/constants';
import { QueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { FrontendSharedEvents } from 'shared/client/consts/frontendSharedEvents';
import { serializeError } from 'shared/client/utils/serializeError';
import { logFrontendEventApi } from 'shared/queries/logFrontendEventApi';

import { getQueryClient } from '@/app/getQueryClient';
import i18n from '@/i18n';

export const Route = createFileRoute('/player/courses/$courseDraftIdentifier/$categoryId/$activityId')({
  loader: async ({ params }) => {
    const queryClient = getQueryClient();

    try {
      const typeData = await queryClient.ensureQueryData({
        queryKey: courseLearningObjectTypeQueryKey(params.activityId),
        queryFn: () => getCourseLearningObjectType(params.activityId)
      });

      const type = typeData?.learningObjectType;

      if (type === LearningObjectTypes.LearningActivity) {
        return { snapshot: await loadLearningActivitySnapshot(queryClient, params.activityId) };
      }
      if (type === LearningObjectTypes.PlenaryLesson) {
        return { snapshot: await loadPlenarySnapshot(queryClient, params.activityId) };
      }
      if (type === LearningObjectTypes.PBL) {
        return { snapshot: await loadPBLSnapshot(queryClient, params.activityId) };
      }
    } catch (error) {
      void logFrontendEventApi({
        eventType: FrontendSharedEvents.player.activityLoadError,
        url: window.location.href,
        ...(error instanceof Error ? { serializedError: serializeError(error) } : {})
      });
    }

    return { snapshot: null };
  },
  head: ({ loaderData }) => {
    const snapshot = loaderData?.snapshot;
    const title = snapshot?.title ?? i18n.t('pages.common:headTitle', { defaultValue: 'Hendrx' });

    return {
      meta: [
        {
          title
        },
        {
          property: 'og:title',
          content: title
        }
      ]
    };
  },
  component: PlayerCourseActivityPageComponent
});

async function loadLearningActivitySnapshot(queryClient: QueryClient, activityId: string) {
  const snapshotRef = await queryClient.ensureQueryData({
    queryKey: latestLearningActivitySnapshotIdQueryKey({ learningActivityDraftId: activityId, publishedOnly: true }),
    queryFn: () => getLatestLearningActivitySnapshotId({ learningActivityDraftId: activityId, publishedOnly: true })
  });
  if (!snapshotRef?._id) return null;
  return queryClient.ensureQueryData({
    queryKey: learningActivitySnapshotByIdQueryKey(snapshotRef._id),
    queryFn: () => getLearningActivitySnapshotById({ learningActivitySnapshotId: snapshotRef._id })
  });
}

async function loadPlenarySnapshot(queryClient: QueryClient, activityId: string) {
  const snapshotRef = await queryClient.ensureQueryData({
    queryKey: latestPlenarySnapshotIdQueryKey({ plenaryDraftId: activityId, publishedOnly: true }),
    queryFn: () => getLatestPlenarySnapshotId({ plenaryDraftId: activityId, publishedOnly: true })
  });
  if (!snapshotRef?._id) return null;
  return queryClient.ensureQueryData({
    queryKey: plenarySnapshotByIdQueryKey(snapshotRef._id),
    queryFn: () => getPlenarySnapshotById({ plenarySnapshotId: snapshotRef._id })
  });
}

async function loadPBLSnapshot(queryClient: QueryClient, activityId: string) {
  const snapshotRef = await queryClient.ensureQueryData({
    queryKey: latestPBLSnapshotIdQueryKey({ pblDraftId: activityId, publishedOnly: true }),
    queryFn: () => getLatestPBLSnapshotId({ pblDraftId: activityId, publishedOnly: true })
  });
  if (!snapshotRef?._id) return null;
  return queryClient.ensureQueryData({
    queryKey: pblSnapshotByIdQueryKey(snapshotRef._id),
    queryFn: () => getPBLSnapshotById({ pblSnapshotId: snapshotRef._id })
  });
}

function PlayerCourseActivityPageComponent() {
  const { activityId } = Route.useParams();

  return <PlayerCourseActivityPage activityId={activityId} />;
}
